/** @format */

import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { PaymentProvider } from "./contexts/PaymentContext";
import { ChatProvider, useChat } from "./contexts/ChatContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import LandingPage from "./pages/LandingPage";
import SignUpPage from "./pages/SignUpPage";
import SignInPage from "./pages/SignInPage";
import PostSignUpPage from "./pages/PostSignUpPage";
import StudentDashboard from "./pages/StudentDashboard";
import MyCourses from "./pages/MyCourses";
import MyBooks from "./pages/MyBooks";
import CourseCatalog from "./pages/CourseCatalog";
import CourseDetail from "./pages/CourseDetail";
import Shop from "./pages/Shop";
import BlogPage from "./pages/Blog";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import OfflinePaymentSuccess from "./pages/OfflinePaymentSuccess";
import OrderValidation from "./pages/OrderValidation";
import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";
import Inbox from "./pages/Inbox";
import AffiliatePage from "./pages/AffiliatePage";
import WithdrawalPage from "./pages/WithdrawalPage";
import Classrooms from "./pages/Classrooms";
import ClassroomDetail from "./pages/ClassroomDetail";
import Feed from "./pages/Feed";
import AILearningDashboard from "./pages/AILearningDashboard";
import CourseLearning from "./pages/CourseLearning";
import MyOrders from "./pages/MyOrders";
import OrderDetails from "./pages/OrderDetails";
import Mentorship from "./pages/Mentorship";
import FreeResources from "./pages/FreeResources";
import StudyForums from "./pages/StudyForums";
import LiveMeetings from "./pages/LiveMeetings";
import Testimonials from "./pages/Testimonials";
import Gallery from "./pages/Gallery";
import AdminPanel from "./pages/AdminPanel";
import TermsAndConditions from "./pages/TermsAndConditions";
import AboutUs from "./pages/AboutUs";
import LegalInfo from "./pages/LegalInfo";
import ForgotPassword from "./pages/ForgotPassword";
import DeviceTest from "./pages/DeviceTest";
import PracticePage from "./pages/PracticePage";

// Components
import Layout from "./components/Layout";
import PublicHeader from "./components/PublicHeader";
import AIChatbot from "./components/AIChatbot";
import AIChatButton from "./components/AIChatButton";
import CartSidebar from "./components/CartSidebar";
import EmbargoOverlay from "./components/EmbargoOverlay";
import { shouldShowIOSWebViewAlert } from "./utils/deviceDetection";

function AppContent() {
  const { isOpen, closeChat, messages, isTyping, error, clearError, sendMessage, fullScreen } = useChat();
  const location = useLocation();
  const isCourseLearning = location.pathname.startsWith("/app/course-learning");

  return (
    <div className="App">
        <Toaster position="top-right" />
        <EmbargoOverlay />
        
        {/* PublicHeader appears on all pages except CourseLearning */}
        {!isCourseLearning && <PublicHeader />}
        
        <Routes>
          {/* Public Routes - Always accessible */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/post-signup" element={<PostSignUpPage />} />
          
          {/* Public content routes - No authentication required */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/catalog" element={<CourseCatalog />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/courses/:courseId" element={<CourseDetail />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/classrooms" element={<Classrooms />} />
          <Route path="/classrooms/:classroomId" element={<ClassroomDetail />} />
          <Route path="/study-forums" element={<StudyForums />} />
          <Route path="/live-meetings" element={<LiveMeetings />} />
          <Route path="/ai-assistant" element={<AILearningDashboard />} />
          <Route path="/mentorship" element={<Mentorship />} />
          <Route path="/legal" element={<LegalInfo />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/free-resources" element={<FreeResources />} />
          <Route path="/device-test" element={<DeviceTest />} />
          
          {/* Protected Routes - Require authentication */}
          <Route
            path="/app/admin"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdminPanel />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app"
            element={
              <Layout>
                <StudentDashboard />
              </Layout>
            }
          />
          <Route
            path="/app/courses"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyCourses />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/books"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyBooks />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/catalog"
            element={
              <ProtectedRoute>
                <Layout>
                  <CourseCatalog />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/shop"
            element={
              <ProtectedRoute>
                <Layout>
                  <Shop />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/blog"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlogPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/live"
            element={
              <ProtectedRoute>
                <Layout>
                  <LiveMeetings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/classrooms"
            element={
              <ProtectedRoute>
                <Layout>
                  <Classrooms />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/classrooms/:classroomId"
            element={
              <ProtectedRoute>
                <ClassroomDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/feed"
            element={
              <ProtectedRoute>
                <Layout>
                  <Feed />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/ai-learning"
            element={
              <ProtectedRoute>
                <Layout>
                  <AILearningDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyOrders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/orders/:orderId"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrderDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/mentorship"
            element={
              <ProtectedRoute>
                <Layout>
                  <Mentorship />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/free-resources"
            element={
              <ProtectedRoute>
                <Layout>
                  <FreeResources />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/practice"
            element={
              <ProtectedRoute>
                <Layout>
                  <PracticePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/forums"
            element={
              <ProtectedRoute>
                <Layout>
                  <StudyForums />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/course-learning/:courseId"
            element={
              <ProtectedRoute>
                <CourseLearning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/course-learning"
            element={
              <ProtectedRoute>
                <div className="p-8 text-center">
                  <h1 className="text-2xl font-bold mb-4">Course Learning</h1>
                  <p>Please select a course to start learning</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/cart"
            element={
              <ProtectedRoute>
                <Layout>
                  <Cart />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/checkout"
            element={
              <ProtectedRoute>
                <Layout>
                  <Checkout />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/payment-success"
            element={
              <ProtectedRoute>
                <Layout>
                  <PaymentSuccess />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/offline-payment-success"
            element={
              <ProtectedRoute>
                <Layout>
                  <OfflinePaymentSuccess />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/order-validation"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrderValidation />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Profile route - accessible from navbar */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Public Profile route - accessible to everyone */}
          <Route path="/profile/:userId" element={<PublicProfile />} />

          {/* Withdrawal route - accessible from profile */}
          <Route
            path="/withdraw"
            element={
              <ProtectedRoute>
                <Layout>
                  <WithdrawalPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Inbox - Direct Messages */}
          <Route
            path="/app/inbox"
            element={
              <ProtectedRoute>
                <Layout>
                  <Inbox />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Redirect to landing page if no route matches */}
          <Route path="*" element={<LandingPage />} />
        </Routes>
        {!shouldShowIOSWebViewAlert() && <CartSidebar />}

        {/* Global AI Chatbot */}
        {isOpen && (
          <AIChatbot 
            isOpen={isOpen}
            onClose={closeChat}
            chatHistory={messages}
            isTyping={isTyping}
            error={error}
            onClearError={clearError}
            onSendMessage={sendMessage}
            fullScreen={fullScreen}
          />
        )}
        
        {/* Global AI Chat Toggle Button */}
        <AIChatButton />
      </div>
  );
}

function App() {
  // Temporary Diagnostic Log
  console.log('=== Chat System Verification ===');
  console.log('Profstudymate AI Key Loaded:', !!process.env.REACT_APP_GEMINI_API_KEY);
  if (process.env.REACT_APP_GEMINI_API_KEY) {
    console.log('Key Ends with:', process.env.REACT_APP_GEMINI_API_KEY.slice(-4));
  }
  console.log('=================================');

  return (
    <AuthProvider>
      <CartProvider>
        <PaymentProvider>
          <ChatProvider>
            <Router>
              <AppContent />
            </Router>
          </ChatProvider>
        </PaymentProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
